import { NeedDto } from '@act/shared/data-transfer-objects';
import {
  Action,
  ActionMenu,
  FlexContainer,
  SmartContentDisplay,
} from '@act/storybook';
import DangerousIcon from '@mui/icons-material/Dangerous';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  useTheme,
} from '@mui/material';
import { ForwardedRef, forwardRef, useEffect } from 'react';
import HtmlDisplayer from 'react-components/components/html-displayer';
import useConfirmationAlertDialog from 'react-components/dialog-forms/hooks/use-confirmation-alert-dialog';
import useCreateNeedFormDialog from 'react-components/dialog-forms/hooks/use-create-need-form-dialog';
import useFulfillNeedDialog from 'react-components/dialog-forms/hooks/use-fulfill-need-dialog';
import { needRequests } from 'redux/requests/need.requests';
import { membersAppSelectors } from 'redux/slices/memberSlice';
import { selectNeedsMetadata, setToast } from 'redux/slices/metaSlice';
import { useAppDispatch, useAppSelector } from 'redux/store-hooks';
import { useApiCall } from 'redux/utils/api-hook';
import NeedStatusTag from './components/need-status-tag';
import NeedDetails from './syva-need-details';
import syvaNeedsUtils from './syva-needs-utils';

interface NeedCardProps {
  need: NeedDto;
}

const NeedCard = forwardRef<HTMLDivElement, NeedCardProps>(
  ({ need }: NeedCardProps, ref: ForwardedRef<HTMLDivElement>) => {
    const theme = useTheme();
    const dispatch = useAppDispatch();

    const member = membersAppSelectors.useSelectMemberById(need.memberId);
    const [_payload, _isLoading, patchNeed] = useApiCall(
      needRequests.patchNeed
    );

    const issues = useAppSelector(selectNeedsMetadata)?.data?.issues;
    const openAbandonNeedDialog = useConfirmationAlertDialog();
    const openDeleteNeedDialog = useConfirmationAlertDialog();
    const openCreateNeedFormDialog = useCreateNeedFormDialog();
    const openFulfillNeedDialog = useFulfillNeedDialog();
    const [deletedNeed, _isDeletingNeed, deleteNeedRequest] = useApiCall(
      needRequests.deleteNeed
    );

    useEffect(() => {
      if (deletedNeed) {
        dispatch({
          type: 'needs/userNeedDeleted',
          payload: { needId: need.id },
        });
      }
    }, [deletedNeed, dispatch, need.id]);

    const actions: Action[] = [
      {
        display: 'Mark need as fulfilled',
        disabled:
          !syvaNeedsUtils.needIsEditable(need, member) || !!need?.fulfilledAt,
        callback: async () => {
          await openFulfillNeedDialog({ needId: need.id });
        },
        ddTrackingName: 'need-card-fulfill-need',
      },
      {
        display: 'Stop pursuing',
        disabled:
          !syvaNeedsUtils.needIsEditable(need, member) || !!need?.fulfilledAt,
        callback: async () => {
          const shouldAbandon = await openAbandonNeedDialog({
            title: 'Stop pursuing need',
            message: 'Are you sure you want to stop pursuing this need?',
            icon: <DangerousIcon />,
            iconColor: 'error',
          });

          if (shouldAbandon) {
            try {
              patchNeed({
                body: { abandoned: true },
                params: { needId: need?.id },
              });
            } catch (err) {
              console.error('Error abandoning need', err);
            }
          }
        },
        ddTrackingName: 'need-card-stop-pursuing',
      },
      {
        display: 'Edit need',
        disabled: !syvaNeedsUtils.needIsEditable(need, member),
        callback: async () => {
          await openCreateNeedFormDialog({ need });
        },
        ddTrackingName: 'need-card-edit-need',
      },
    ];

    if (need.canDelete) {
      actions.push({
        display: 'Delete need',
        callback: async () => {
          const deleteNeedIsConfirmed = await openDeleteNeedDialog({
            title: 'Confirm delete',
            icon: <ErrorOutlineIcon />,
            iconColor: 'error',
            iconBorderColor: 'errorContainer',
            avatarCaption: 'Are you sure?',
            message: 'Are you sure you want to delete this need?',
            confirmationText: 'Yes, delete it',
            confirmButtonColor: 'error',
          });
          if (deleteNeedIsConfirmed) {
            deleteNeedRequest({
              params: { needId: need.id },
            });
          }
        },
      });
    } else {
      actions.push({
        display: 'Delete need',
        displayAsDisabled: true,
        callback: async () => {
          dispatch(
            setToast({
              message:
                'Needs may only be deleted if they are created today, and have no actions performed on them',
              severity: 'info',
            })
          );
        },
      });
    }

    return (
      <Card
        ref={ref}
        component="div"
        className="need-card"
        sx={{
          '@keyframes bg-blink-text': {
            '50%': {
              color: `${theme.palette.greyShades?.['100']}`,
            },
          },
          '@keyframes bg-blink': {
            '50%': {
              backgroundColor: `${theme.palette.navyShades?.['200']}`,
            },
          },
          '&.highlighted, &.highlighted .MuiAccordion-root': {
            marginTop: '4px',
            animation: 'bg-blink .5s 5, bg-blink-text .5s 5',
            animationDelay: '1s',

            '&.highlighted .MuiButton-root': {
              animation: 'bg-blink-text .5s 5',
              animationDelay: '1s',
            },
          },
        }}
      >
        <CardHeader
          className="needs-info__header"
          title={
            <FlexContainer direction="row" gap={1} alignItems="center">
              <Typography variant="titleMedium">
                {issues?.find(i => i.id === need.issueId)?.label}
              </Typography>
              <NeedStatusTag need={need} />
            </FlexContainer>
          }
          subheader={
            <Typography variant="bodyMedium">
              {syvaNeedsUtils.formatNeedDateLine(need, member)}
            </Typography>
          }
          action={<ActionMenu actions={actions} horizontalAlign="right" />}
        ></CardHeader>
        <CardContent>
          <SmartContentDisplay maxHeight={150}>
            <Typography variant="bodyLarge" component="div">
              {<HtmlDisplayer innerHtml={need.reason} />}
            </Typography>
          </SmartContentDisplay>
        </CardContent>
        <NeedDetails need={need} />
      </Card>
    );
  }
);

NeedCard.displayName = 'NeedCard';

export default NeedCard;
